import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { FiChevronDown, FiChevronRight, FiEye, FiPlus } from "react-icons/fi";
import { RiRefreshLine } from "react-icons/ri";
import {
  FaArrowLeft,
  FaArrowRight,
  FaArrowUpRightFromSquare,
} from "react-icons/fa6";
import { LuCheck, LuHourglass, LuLandmark, LuUsers } from "react-icons/lu";
import type { PayoutAccount } from "./payoutData";
import PayoutAccountOverview from "./PayoutAccountOverview";
import PayoutAddAccount from "./PayoutAddAccount";
import PayoutAddBeneficiary from "./PayoutAddBeneficiary";
import PayoutAddBeneficiaryConfirm from "./PayoutAddBeneficiaryConfirm";
import PayoutAddBeneficiaryReview from "./PayoutAddBeneficiaryReview";
import PayoutAddBeneficiaryVerify from "./PayoutAddBeneficiaryVerify";
import PayoutAddUser from "./PayoutAddUser";
import PayoutApprovalActionConfirm from "./PayoutApprovalActionConfirm";
import PayoutApprovalActionVerify from "./PayoutApprovalActionVerify";
import PayoutApprovalRules from "./PayoutApprovalRules";
import PayoutBeneficiaries from "./PayoutBeneficiaries";
import {
  PayoutBeneficiaryDetails,
  PayoutDeleteBeneficiaryConfirm,
  PayoutDeleteBeneficiaryVerify,
  PayoutEditBeneficiary,
  PayoutEditBeneficiaryConfirm,
  PayoutEditBeneficiaryReview,
  PayoutEditBeneficiaryVerify,
} from "./PayoutBeneficiaryActionPages";
import PayoutBeneficiaryBulkUpload from "./PayoutBeneficiaryBulkUpload";
import PayoutBeneficiaryBulkUploadConfirm from "./PayoutBeneficiaryBulkUploadConfirm";
import PayoutBeneficiaryBulkUploadReview from "./PayoutBeneficiaryBulkUploadReview";
import PayoutBeneficiaryBulkUploadVerify from "./PayoutBeneficiaryBulkUploadVerify";
import PayoutBulkUploadConfirm from "./PayoutBulkUploadConfirm";
import PayoutBulkUploadReview from "./PayoutBulkUploadReview";
import PayoutBulkUploadTransfer from "./PayoutBulkUploadTransfer";
import PayoutBulkUploadVerify from "./PayoutBulkUploadVerify";
import PayoutBulkApprovalVerify from "./PayoutBulkApprovalVerify";
import PayoutConfirmTransfer from "./PayoutConfirmTransfer";
import PayoutInitiateTransfer from "./PayoutInitiateTransfer";
import PayoutPendingApproval from "./PayoutPendingApproval";
import PayoutPermissions from "./PayoutPermissions";
import PayoutRoleDetails from "./PayoutRoleDetails";
import PayoutReviewTransfer from "./PayoutReviewTransfer";
import PayoutRoles from "./PayoutRoles";
import PayoutReports, { PayoutInsights } from "./PayoutReports";
import PayoutSettings from "./PayoutSettings";
import PayoutStatements, {
  StatementDetailsDrawer,
  StatementDetailsRow,
} from "./PayoutStatements";
import PayoutTransfers from "./PayoutTransfers";
import PayoutUsers from "./PayoutUsers";
import {
  PayoutChangeUserPassword,
  PayoutDeleteUserComplete,
  PayoutDeleteUserConfirm,
  PayoutEditUser,
  PayoutUserActionVerify,
  PayoutUserDetails,
} from "./PayoutUserActionPages";
import PayoutVerifyTransfer from "./PayoutVerifyTransfer";
import PayoutDataTable from "./PayoutDataTable";
import PayoutEmptyState from "./PayoutEmptyState";
import PayoutKpiValue from "./PayoutKpiValue";
import PayoutSearchFilter from "./PayoutSearchFilter";
import PayoutNotification from "./PayoutNotification";
import PayoutTableCell from "./PayoutTableCell";
import { payoutApi } from "./api/payoutApiClient";
import {
  accountFromApiRecord,
  apiNumber,
  apiString,
  formatApiDateTime,
  transferRowFromApiRecord,
} from "./api/payoutApiAdapters";
import {
  loadDashboardViewData,
  emptyDashboardViewData,
} from "./api/payoutViewData";
import { usePayoutResource } from "./api/usePayoutResource";
import {
  usePayoutAccess,
  type PayoutPermission,
} from "./payoutAccessControl";

export const payoutStatusClassName: Record<string, string> = {
  Processed: "bg-[#DCFCE7] text-[#16A34A]",
  Processing: "bg-[#DBEAFE] text-[#0284C7]",
  Pending: "bg-[#FEF3C7] text-[#F59E0B]",
  Failed: "bg-[#FEE2E2] text-[#EF4444]",
};

const formatINR = (amount: number, decimals = false) =>
  `\u20B9${amount.toLocaleString("en-IN", {
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: decimals ? 2 : 0,
  })}`;

const navClass = ({ isActive }: { isActive: boolean }) =>
  "py-1.5 font-semibold rounded-lg text-[14px] text-left pl-10 transition-colors " +
  (isActive
    ? "bg-[#6687FF33] bg-opacity-20 text-[#1B163B]"
    : "text-[#717171]");

const subNavClass = ({ isActive }: { isActive: boolean }) =>
  "py-2 font-medium rounded-md text-[13px] text-left pl-12 transition-colors " +
  (isActive
    ? "bg-[#6687FF33] bg-opacity-20 text-[#1B163B]"
    : "text-[#505E8E]");

function PayoutModuleSidebar({ menu }: { menu: boolean }) {
  const location = useLocation();
  const { visibleLinks } = usePayoutAccess();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  return (
    <div
      className={
        "transition-transform duration-200 fixed overflow-hidden pt-10 " +
        (menu ? "translate-x-0 w-56" : "-translate-x-[100%] w-0")
      }
    >
      <div className="flex min-h-screen shrink-0 flex-col">
        <nav className="flex flex-col">
          {visibleLinks.map((item) => {
            const isTransferPath =
              location.pathname.startsWith("/payouts/transfers");
            const isAccountOverviewPath =
              location.pathname.startsWith("/payouts/accounts/");
            const isUsersPath =
              location.pathname.startsWith("/payouts/users") ||
              location.pathname.startsWith("/payouts/roles") ||
              location.pathname.startsWith("/payouts/permissions");
            const isApprovalRulesPath =
              location.pathname.startsWith("/payouts/approval-rules");
            const isReportsPath =
              location.pathname.startsWith("/payouts/reports");
            const isUsersGroup = item.label === "Users & Roles" && isUsersPath;
            const isApprovalRulesGroup =
              item.to === "/payouts/approval-rules" && isApprovalRulesPath;
            const isReportsGroup = item.label === "Reports" && isReportsPath;
            const isDashboardGroup =
              item.to === "/payouts/dashboard" && isAccountOverviewPath;
            const isGroupActive =
              isDashboardGroup ||
              (item.to === "/payouts/transfers" && isTransferPath) ||
              isUsersGroup ||
              isApprovalRulesGroup ||
              isReportsGroup;
            const isExpanded = Boolean(
              item.children && (expandedGroups[item.label] ?? isGroupActive),
            );

            return (
              <React.Fragment key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={(event) => {
                    if (!item.children) return;

                    if (isGroupActive) {
                      event.preventDefault();
                      setExpandedGroups((current) => ({
                        ...current,
                        [item.label]: !isExpanded,
                      }));
                      return;
                    }

                    setExpandedGroups((current) => ({
                      ...current,
                      [item.label]: true,
                    }));
                  }}
                  className={({ isActive }) =>
                    navClass({ isActive: isActive || isGroupActive }) +
                    (item.children
                      ? " flex items-center justify-between pr-4"
                      : "")
                  }
                >
                  <span>{item.label}</span>
                  {item.children &&
                    (isExpanded ? (
                      <FiChevronDown className="text-base" />
                    ) : (
                      <FiChevronRight className="text-base" />
                    ))}
                </NavLink>
                {item.children && isExpanded && (
                  <div className="mt-1 mb-2 flex flex-col gap-1">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.to + child.label}
                        to={child.to}
                        className={({ isActive }) =>
                          subNavClass({
                            isActive:
                              (child.to === "/payouts/transfers"
                                ? location.pathname === "/payouts/transfers"
                                : child.to === "/payouts/reports"
                                  ? location.pathname === "/payouts/reports"
                                  : child.to === "/payouts/reports/insights"
                                    ? location.pathname.startsWith(
                                        "/payouts/reports/insights",
                                      )
                                : isActive) ||
                              (child.to === "/payouts/users" &&
                                location.pathname.startsWith("/payouts/users")) ||
                              (child.to === "/payouts/roles" &&
                                location.pathname.startsWith("/payouts/roles")) ||
                              (child.to === "/payouts/permissions" &&
                                location.pathname.startsWith("/payouts/permissions")) ||
                              (child.to === "/payouts/approval-rules/workflow-rules" &&
                                location.pathname.startsWith(
                                  "/payouts/approval-rules/workflow-rules",
                                )),
                          })
                        }
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

const emptyAccountFallback: PayoutAccount = {
  id: "",
  name: "",
  school: "",
  bank: "",
  logoUrl: "",
  accountNumber: "",
  ifsc: "",
  currency: "INR",
  addedOn: "",
  availableBalance: 0,
  accountBalance: 0,
  fundsOnHold: 0,
  overdraftBalance: 0,
  overdraftLimit: 0,
  lowBalanceThreshold: 0,
  accent: "",
};

const maskAccountNumber = (accountNumber: string) => {
  const trimmed = accountNumber.trim();
  if (!trimmed) return "";
  const last4 = trimmed.slice(-4);
  return `${"*".repeat(Math.max(trimmed.length - 4, 0))}${last4}`;
};

const displayText = (value?: unknown) => String(value ?? "").trim() || "-";

const joinDisplayText = (values: string[]) =>
  values.map((value) => value.trim()).filter(Boolean).join(" - ") || "-";

type DashboardPendingApprovalRow = {
  transferId: string;
  beneficiary: string;
  purpose: string;
  account: string;
  accountDetail: string;
  amount: number;
  requestedBy: string;
  requestedAt: string;
  priority: string;
};

const emptyDashboardPendingApproval: DashboardPendingApprovalRow = {
  transferId: "",
  beneficiary: "",
  purpose: "",
  account: "",
  accountDetail: "",
  amount: 0,
  requestedBy: "",
  requestedAt: "",
  priority: "",
};

const apiRecordValue = (
  row: Record<string, unknown>,
  key: string,
): Record<string, unknown> => {
  const value = row[key];
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
};

const pendingPriorityFromAmount = (amount: number) =>
  amount > 150000
    ? "Urgent"
    : amount > 100000
      ? "High"
      : amount > 50000
        ? "Medium"
        : "Low";

const dashboardPendingApprovalFromApiRecord = (
  row: Record<string, unknown>,
): DashboardPendingApprovalRow => {
  const funding = apiRecordValue(row, "funding_account");
  const beneficiary = apiRecordValue(row, "beneficiary");
  const approvalUser = apiRecordValue(row, "approval_user");
  const transfer = transferRowFromApiRecord(row, emptyDashboardPendingApproval);
  const fundingName = apiString(
    funding,
    ["display_name", "displayName", "name", "account_name", "accountName"],
    "",
  );
  const fundingMasked =
    apiString(funding, ["account_number_masked", "accountNumberMasked"], "") ||
    apiString(funding, ["account_number_last4", "accountNumberLast4", "last4"], "");
  const beneficiaryName = apiString(
    beneficiary,
    ["name", "beneficiary_name", "beneficiaryName", "name_entered", "nameEntered"],
    "",
  );
  const amount = apiNumber(
    row,
    ["amount_paise", "amountPaise", "amount"],
    Number(transfer.amount || 0),
    Boolean(row.amount_paise || row.amountPaise),
  );

  return {
    transferId: displayText(transfer.transferId || transfer.referenceId),
    beneficiary: displayText(beneficiaryName || transfer.beneficiary),
    purpose: displayText(transfer.purpose),
    account: displayText(fundingName || transfer.payoutAccount),
    accountDetail: displayText(fundingMasked || transfer.payoutAccountDetail),
    amount,
    requestedBy: displayText(
      apiString(
        row,
        [
          "requested_by_name",
          "requestedByName",
          "maker_name",
          "makerName",
          "created_by_name",
          "createdByName",
        ],
        "",
      ) ||
        apiString(approvalUser, ["name", "full_name", "fullName"], "") ||
        apiString(row, ["maker_user_id", "makerUserId"], ""),
    ),
    requestedAt: formatApiDateTime(
      apiString(row, ["requested_at", "requestedAt", "created_at", "createdAt"], ""),
      displayText(transfer.createdAt),
    ),
    priority: displayText(apiString(row, ["priority"], "") || pendingPriorityFromAmount(amount)),
  };
};

const dashboardApprovalVerifyPath = (
  action: "approve" | "reject",
  row: DashboardPendingApprovalRow,
) => {
  const params = new URLSearchParams({
    transferId: row.transferId,
    beneficiary: row.beneficiary,
    amount: formatINR(row.amount, true),
  });
  return `/payouts/transfers/pending-approval/${action}/verify?${params.toString()}`;
};

function PayoutAccountCard({ account }: { account: PayoutAccount }) {
  return (
    <div className="relative min-h-[176px] rounded-[8px] border border-[#E0E5F2] bg-white px-5 py-5 new-shadow">
      <div className="flex gap-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[4px] bg-white">
          <img
            src={account.logoUrl}
            alt={`${account.bank} logo`}
            className="h-full w-full object-contain"
          />
        </div>
        <div>
          <p className="text-sm font-semibold leading-5 text-[#1B163B]">
            {account.name}
          </p>
          <p className="mt-1.5 text-xs text-[#505E8E]">{account.bank}</p>
          <p className="mt-3.5 text-sm font-semibold tracking-[3px] text-[#505E8E]">
            {maskAccountNumber(account.accountNumber)}
          </p>
        </div>
      </div>

      <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
        <div>
          <p className="text-xs font-medium text-[#505E8E]">
            Available Balance
          </p>
          <p className="mt-1.5 text-[22px] font-semibold leading-7 text-[#1B163B]">
            ₹<PayoutKpiValue value={account.availableBalance} />
          </p>
        </div>
        <Link
          to={`/payouts/accounts/${account.id}`}
          className="flex h-7 w-7 items-center justify-center rounded-[6px] bg-[#6687FF1A] text-[#6687FF]"
          title={`View ${account.name}`}
        >
          <FaArrowUpRightFromSquare className="text-xs" />
        </Link>
      </div>
    </div>
  );
}

function PayoutDashboardHome() {
  const { canAccess } = usePayoutAccess();
  const canApproveTransfers = canAccess("transfers.approve");
  const dashboardResource = usePayoutResource(
    emptyDashboardViewData,
    loadDashboardViewData,
  );
  const dashboardData = dashboardResource.data;
  const dashboardAccounts = dashboardData.accounts;
  const dashboardRecentPayouts = dashboardData.recentPayouts;
  const dashboardQuickStats = useMemo(
    () =>
      dashboardData.quickStats.map((stat, index) => ({
        ...stat,
        icon:
          [
            <LuLandmark />,
            <LuHourglass />,
            <LuCheck />,
            <LuUsers />,
          ][index] || <LuCheck />,
        tone: "bg-[#6687FF] text-white",
      })),
    [dashboardData.quickStats],
  );
  const pendingApprovalsStat =
    dashboardQuickStats.find((stat) => stat.label === "Pending Approvals") ||
    dashboardQuickStats[1];
  const secondaryDashboardStats = dashboardQuickStats.filter(
    (stat) => stat.label !== "Pending Approvals",
  );
  const dashboardAccountPreview = useMemo(
    () =>
      [...dashboardAccounts]
        .sort((first, second) => {
          const firstTime = Date.parse(first.addedOn || "");
          const secondTime = Date.parse(second.addedOn || "");
          if (Number.isNaN(firstTime) && Number.isNaN(secondTime)) return 0;
          if (Number.isNaN(firstTime)) return 1;
          if (Number.isNaN(secondTime)) return -1;
          return secondTime - firstTime;
        })
        .slice(0, 4),
    [dashboardAccounts],
  );
  const [recentPayoutPage, setRecentPayoutPage] = useState(1);
  const [selectedStatement, setSelectedStatement] = useState<StatementDetailsRow | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState("03:23 PM");
  const [recentPayoutSearchQuery, setRecentPayoutSearchQuery] = useState("");
  const [recentPayoutSearchField, setRecentPayoutSearchField] =
    useState("Reference ID");
  const [pendingApprovalPage, setPendingApprovalPage] = useState(1);
  const [pendingApprovalSearchQuery, setPendingApprovalSearchQuery] = useState("");
  const [pendingApprovalSearchField, setPendingApprovalSearchField] =
    useState("Transfer ID");
  const recentPayoutPageSize = 5;
  const pendingApprovalPageSize = 5;
  const pendingApprovalsResource = usePayoutResource<DashboardPendingApprovalRow[]>(
    [],
    (signal) =>
      canApproveTransfers
        ? payoutApi
            .pendingApprovals({ limit: 25 }, signal)
            .then((rows) => rows.map(dashboardPendingApprovalFromApiRecord))
        : Promise.resolve([]),
    [canApproveTransfers],
  );
  const paginatedRecentPayouts = useMemo(
    () => {
      if (!dashboardRecentPayouts.length) return [];
      return dashboardRecentPayouts;
    },
    [dashboardRecentPayouts],
  );
  const recentPayoutSearchOptions = [
    "Reference ID",
    "UTR Number",
    "Beneficiary Account Number",
    "Beneficiary Name",
  ];
  const pendingApprovalSearchOptions = [
    "Transfer ID",
    "Beneficiary Name",
    "Requested By",
    "Amount",
  ];
  const getRecentPayoutSearchValue = useCallback((
    payout: (typeof paginatedRecentPayouts)[number],
  ) => {
    const account = dashboardAccounts.find((item) => item.id === payout.accountId);
    switch (recentPayoutSearchField) {
      case "UTR Number":
        return payout.utrNumber;
      case "Beneficiary Account Number":
        return account?.accountNumber || "";
      case "Beneficiary Name":
        return payout.beneficiary;
      case "Reference ID":
      default:
        return payout.referenceId;
    }
  }, [dashboardAccounts, recentPayoutSearchField]);
  const filteredRecentPayouts = useMemo(() => {
    const query = recentPayoutSearchQuery.trim().toLowerCase();
    if (!query) return paginatedRecentPayouts;

    return paginatedRecentPayouts.filter((payout) =>
      getRecentPayoutSearchValue(payout).toLowerCase().includes(query),
    );
  }, [getRecentPayoutSearchValue, paginatedRecentPayouts, recentPayoutSearchQuery]);
  const getPendingApprovalSearchValue = useCallback((
    row: DashboardPendingApprovalRow,
  ) => {
    switch (pendingApprovalSearchField) {
      case "Beneficiary Name":
        return row.beneficiary;
      case "Requested By":
        return row.requestedBy;
      case "Amount":
        return String(row.amount);
      case "Transfer ID":
      default:
        return row.transferId;
    }
  }, [pendingApprovalSearchField]);
  const filteredPendingApprovals = useMemo(() => {
    const query = pendingApprovalSearchQuery.trim().toLowerCase();
    if (!query) return pendingApprovalsResource.data;

    return pendingApprovalsResource.data.filter((row) =>
      getPendingApprovalSearchValue(row).toLowerCase().includes(query),
    );
  }, [
    getPendingApprovalSearchValue,
    pendingApprovalSearchQuery,
    pendingApprovalsResource.data,
  ]);
  const recentPayoutPageCount = Math.max(1, Math.ceil(
    filteredRecentPayouts.length / recentPayoutPageSize,
  ));
  const pendingApprovalPageCount = Math.max(1, Math.ceil(
    filteredPendingApprovals.length / pendingApprovalPageSize,
  ));
  const visibleRecentPayouts = filteredRecentPayouts.slice(
    (recentPayoutPage - 1) * recentPayoutPageSize,
    recentPayoutPage * recentPayoutPageSize,
  );
  const visiblePendingApprovals = filteredPendingApprovals.slice(
    (pendingApprovalPage - 1) * pendingApprovalPageSize,
    pendingApprovalPage * pendingApprovalPageSize,
  );
  const recentPayoutStart = filteredRecentPayouts.length
    ? (recentPayoutPage - 1) * recentPayoutPageSize + 1
    : 0;
  const recentPayoutEnd = Math.min(
    recentPayoutPage * recentPayoutPageSize,
    filteredRecentPayouts.length,
  );
  const pendingApprovalStart = filteredPendingApprovals.length
    ? (pendingApprovalPage - 1) * pendingApprovalPageSize + 1
    : 0;
  const pendingApprovalEnd = Math.min(
    pendingApprovalPage * pendingApprovalPageSize,
    filteredPendingApprovals.length,
  );
  useEffect(() => {
    setRecentPayoutPage(1);
  }, [recentPayoutSearchQuery, recentPayoutSearchField]);
  useEffect(() => {
    setPendingApprovalPage(1);
  }, [pendingApprovalSearchQuery, pendingApprovalSearchField]);
  const openStatementDetails = (payout: (typeof visibleRecentPayouts)[number]) => {
    const account = dashboardAccounts.find((item) => item.id === payout.accountId);
    setSelectedStatement({
      dateTime: payout.settlementTime || "-",
      referenceId: displayText(payout.referenceId),
      utrNumber: displayText(payout.utrNumber),
      beneficiaryAccountNumber: account?.accountNumber || "",
      narration: joinDisplayText([payout.purpose, payout.beneficiary]),
      account: displayText(payout.account),
      accountNumber: account?.accountNumber || "",
      type: "Debit",
      amount: -Math.abs(payout.amount),
      balance: account?.availableBalance || 0,
      status: payout.status === "Processed" ? "Successful" : displayText(payout.status),
    });
  };
  const handleRefresh = () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    dashboardResource.refresh();
    window.setTimeout(() => {
      setLastUpdatedAt(
        new Date().toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      );
      setIsRefreshing(false);
    }, 700);
  };
  const recentPayoutTableRows = visibleRecentPayouts.map((payout) => [
    <PayoutTableCell
      value={displayText(payout.referenceId)}
      onClick={() => openStatementDetails(payout)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <PayoutTableCell
      value={displayText(payout.utrNumber)}
      onClick={() => openStatementDetails(payout)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <PayoutTableCell
      value={displayText(payout.beneficiary)}
      onClick={() => openStatementDetails(payout)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <PayoutTableCell
      value={displayText(payout.purpose)}
      onClick={() => openStatementDetails(payout)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <PayoutTableCell
      value={formatINR(payout.amount)}
      onClick={() => openStatementDetails(payout)}
      valueClassName="font-semibold text-[#1B163B]"
    />,
    <button
      type="button"
      onClick={() => openStatementDetails(payout)}
      className="block w-full text-left"
    >
      <span
        className={
          "rounded-[6px] px-2 py-1 text-xs font-semibold " +
          (payoutStatusClassName[payout.status] || "bg-[#EEF2F7] text-[#505E8E]")
        }
      >
        {displayText(payout.status)}
      </span>
    </button>,
    <PayoutTableCell
      value={displayText(payout.settlementTime)}
      onClick={() => openStatementDetails(payout)}
      valueClassName="font-medium text-[#505E8E]"
    />,
    <div className="payout-action-cell payout-action-cell--single">
      <button
        type="button"
        onClick={() => openStatementDetails(payout)}
        className="payout-action-icon"
        aria-label={`View details for ${payout.referenceId}`}
        title="View details"
      >
        <FiEye className="text-sm" />
      </button>
    </div>,
  ]);
  const pendingApprovalTableRows = visiblePendingApprovals.map((row) => [
    <PayoutTableCell
      value={displayText(row.transferId)}
      valueClassName="font-semibold text-[#1B163B]"
    />,
    <PayoutTableCell
      value={displayText(row.beneficiary)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <PayoutTableCell
      value={displayText(row.purpose)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <span>
      <span className="block font-medium text-[#1B163B]">
        {displayText(row.account)}
      </span>
      <span className="mt-1 block text-[#505E8E]">
        {displayText(row.accountDetail)}
      </span>
    </span>,
    <span className="font-semibold text-[#1B163B]">
      {formatINR(row.amount, true)}
    </span>,
    <PayoutTableCell
      value={displayText(row.requestedBy)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <PayoutTableCell
      value={displayText(row.requestedAt)}
      valueClassName="font-medium text-[#505E8E]"
    />,
    <span
      className={
        "rounded-[6px] px-2 py-1 text-xs font-semibold " +
        (row.priority === "Urgent" || row.priority === "High"
          ? "bg-[#FEE2E2] text-[#EF4444]"
          : "bg-[#FEF3C7] text-[#B45309]")
      }
    >
      {displayText(row.priority)}
    </span>,
    <div className="flex min-w-max items-center gap-2">
      <Link
        to={dashboardApprovalVerifyPath("approve", row)}
        className="flex h-8 min-w-[72px] items-center justify-center rounded-[5px] bg-[#1B163B] px-3 text-xs font-semibold text-white"
      >
        Approve
      </Link>
      <Link
        to={dashboardApprovalVerifyPath("reject", row)}
        className="flex h-8 min-w-[64px] items-center justify-center rounded-[5px] border border-[#E0E5F2] bg-white px-3 text-xs font-semibold text-[#1B163B]"
      >
        Reject
      </Link>
    </div>,
  ]);

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-4">
            <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
              Dashboard
            </h1>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2 text-sm font-medium text-[#6687FF] disabled:cursor-not-allowed disabled:opacity-70"
            >
              <RiRefreshLine
                className={"text-lg " + (isRefreshing ? "animate-spin" : "")}
              />
              Refresh
            </button>
          </div>
          <p className="mt-1 text-sm text-[#717171]">
            Overview of your payout accounts and recent activity
          </p>
          {dashboardResource.error && (
            <p className="mt-2 text-sm font-medium text-[#C0392B]">
              Payouts API: {dashboardResource.error}
            </p>
          )}
        </div>
      </div>

      <div className="mb-8 rounded-[8px] border border-[#E0E5F2] bg-white px-6 py-6 new-shadow">
        <div className="grid gap-5 md:grid-cols-[minmax(270px,300px)_minmax(230px,260px)_1px_minmax(0,1fr)] md:items-center md:gap-x-4">
          <div className="border-b border-[#E0E5F2] pb-5 md:border-b-0 md:pr-1 md:pb-0">
            <p className="text-sm font-medium text-[#505E8E]">
              Total Available Balance
            </p>
            {isRefreshing ? (
              <div className="mt-3 h-8 w-48 animate-pulse rounded-[6px] bg-[#EEF2F7]" />
            ) : (
              <p className="mt-3 text-[28px] font-semibold leading-8 text-[#1B163B]">
                <PayoutKpiValue
                  value={formatINR(
                    dashboardData.totalAvailableBalance,
                    dashboardData.totalAvailableBalance % 1 !== 0,
                  )}
                  compactAt={Number.POSITIVE_INFINITY}
                />
              </p>
            )}
            <div className="mt-4 flex items-center gap-8">
              <p className="text-sm text-[#717171]">
                Last updated at {lastUpdatedAt}
              </p>
            </div>
          </div>

          <div className="border-b border-[#E0E5F2] pb-5 md:border-b-0 md:px-1 md:pb-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-[#505E8E]">
                  Pending Approvals
                </p>
                {isRefreshing ? (
                  <div className="mt-3 h-8 w-36 animate-pulse rounded-[6px] bg-[#EEF2F7]" />
                ) : (
                  <p className="mt-3 text-[28px] font-semibold leading-8 text-[#1B163B]">
                    <PayoutKpiValue
                      value={pendingApprovalsStat?.value || "\u20B90"}
                      compactAt={Number.POSITIVE_INFINITY}
                    />
                  </p>
                )}
                <p className="mt-4 text-sm text-[#717171]">
                  {pendingApprovalsResource.data.length.toLocaleString("en-IN")} requests awaiting action
                </p>
              </div>
              <Link
                to="/payouts/transfers/pending-approval"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] bg-[#6687FF1A] text-[#6687FF]"
                title="View pending approvals"
              >
                <FaArrowUpRightFromSquare className="text-xs" />
              </Link>
            </div>
          </div>

          <div className="hidden h-[92px] w-px bg-[#E0E5F2] md:block" />

          <div className="grid min-w-0 gap-5 sm:grid-cols-3 xl:gap-7">
            {secondaryDashboardStats.map((stat) => (
              <div
                key={stat.label}
                className="flex min-w-0 items-center gap-4 justify-self-center"
              >
                <div
                  className={
                    "payout-kpi-icon flex h-10 w-10 shrink-0 items-center justify-center text-lg " +
                    stat.tone
                  }
                >
                  {stat.icon}
                </div>
                <div className="min-w-0">
                  <p className="whitespace-nowrap text-[12px] font-medium text-[#505E8E]">
                    {stat.label}
                  </p>
                  {isRefreshing ? (
                    <div className="mt-2 h-6 w-20 animate-pulse rounded-[5px] bg-[#EEF2F7]" />
                  ) : (
                    <p className="mt-2 text-lg font-semibold text-[#1B163B]">
                      <PayoutKpiValue value={stat.value} />
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="mb-7">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[20px] font-semibold leading-7 text-[#1B163B]">
            Payout Accounts
          </h2>
          <Link
            to="/payouts/accounts"
            className="flex items-center gap-2 text-sm font-semibold text-[#6687FF]"
          >
            View all accounts
            <FaArrowRight className="text-xs" />
          </Link>
        </div>

        <div>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {dashboardAccountPreview.map((account) => (
            <div
              key={account.id}
              className="flex min-h-[204px] flex-col justify-between rounded-[8px] border border-[#E0E5F2] bg-white px-6 py-6 new-shadow"
            >
              <div className="flex justify-between gap-4">
                <div className="flex gap-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[4px] bg-white">
                    <img
                      src={account.logoUrl}
                      alt={`${account.bank} logo`}
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold leading-5 text-[#1B163B]">
                      {account.name}
                    </p>
                    <p className="mt-1.5 text-xs text-[#505E8E]">
                      {account.bank}
                    </p>
                    <p className="mt-5 text-sm font-semibold tracking-[3px] text-[#505E8E]">
                      {maskAccountNumber(account.accountNumber)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-end justify-between gap-4">
                <div>
                  <p className="text-xs font-medium text-[#505E8E]">
                    Available Balance
                  </p>
                  <p className="mt-1.5 text-[22px] font-semibold leading-7 text-[#1B163B]">
                    ₹<PayoutKpiValue value={account.availableBalance} />
                  </p>
                </div>
                <Link
                  to={`/payouts/accounts/${account.id}`}
                  className="flex h-7 w-7 items-center justify-center rounded-[6px] bg-[#6687FF1A] text-[#6687FF]"
                  title={`View ${account.name}`}
                >
                  <FaArrowUpRightFromSquare className="text-xs" />
                </Link>
              </div>
            </div>
          ))}
          </div>
          {!dashboardAccounts.length && (
            <div className="rounded-[8px] border border-[#E0E5F2] bg-white new-shadow">
              <PayoutEmptyState
                icon={<LuLandmark />}
                title="No payout accounts yet"
                description="Add a payout account before creating transfers or assigning account access to finance users."
                action={
                  canAccess("accounts.create") ? (
                    <Link
                      to="/payouts/accounts/new"
                      className="flex h-10 items-center gap-2 rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
                    >
                      <FiPlus /> New Account
                    </Link>
                  ) : null
                }
              />
            </div>
          )}
        </div>
      </section>

      {canApproveTransfers && (
        <PayoutDataTable
          title="Pending Approvals"
          headerAction={
            <Link
              to="/payouts/transfers/pending-approval"
              className="flex items-center gap-2 text-sm font-semibold text-[#6687FF]"
            >
              View all pending approvals
              <FaArrowRight className="text-xs" />
            </Link>
          }
          headerContent={
            <PayoutSearchFilter
              value={pendingApprovalSearchQuery}
              onChange={setPendingApprovalSearchQuery}
              filterBy={pendingApprovalSearchField}
              onFilterByChange={setPendingApprovalSearchField}
              filterOptions={pendingApprovalSearchOptions}
              placeholder={`Search by ${pendingApprovalSearchField}`}
              className="w-full max-w-[620px]"
            />
          }
          className="payout-dashboard-table mb-7"
          columns={[
            "Transfer ID",
            "Beneficiary",
            "Purpose",
            "Payout Account",
            "Amount",
            "Requested By",
            "Requested At",
            "Priority",
            "Actions",
          ]}
          rows={pendingApprovalTableRows}
          minWidth="min-w-[1180px]"
          emptyState={
            <PayoutEmptyState
              icon={<LuHourglass />}
              title="No pending approvals"
              description="Transfer requests waiting for checker or approver action will appear here."
              action={
                <Link
                  to="/payouts/transfers"
                  className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
                >
                  View Transfers
                </Link>
              }
            />
          }
          footer={
            <div className="flex min-h-[64px] w-full items-center justify-between gap-4 bg-[#F6F8FA] px-6 py-4 text-xs font-medium text-[#505E8E]">
              <span className="min-w-max">
                Showing {pendingApprovalStart} to {pendingApprovalEnd} of{" "}
                {filteredPendingApprovals.length} results
              </span>
              <div className="ml-auto flex min-w-max items-center gap-2">
                <button
                  disabled={pendingApprovalPage === 1}
                  onClick={() =>
                    setPendingApprovalPage((current) => Math.max(1, current - 1))
                  }
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowLeft className="text-xs" />
                </button>
                {Array.from({ length: pendingApprovalPageCount }, (_, index) => {
                  const page = index + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setPendingApprovalPage(page)}
                      className={
                        "h-9 w-9 rounded-md " +
                        (pendingApprovalPage === page
                          ? "bg-[#1B163B] text-white"
                          : "border border-[#E0E5F2] text-[#1B163B]")
                      }
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  disabled={pendingApprovalPage === pendingApprovalPageCount}
                  onClick={() =>
                    setPendingApprovalPage((current) =>
                      Math.min(pendingApprovalPageCount, current + 1),
                    )
                  }
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          }
        />
      )}

      <PayoutDataTable
        title="Recent Payouts"
        headerAction={
          <Link
            to="/payouts/statements"
            className="flex items-center gap-2 text-sm font-semibold text-[#6687FF]"
          >
            View all payouts
            <FaArrowRight className="text-xs" />
          </Link>
        }
        headerContent={
          <PayoutSearchFilter
            value={recentPayoutSearchQuery}
            onChange={setRecentPayoutSearchQuery}
            filterBy={recentPayoutSearchField}
            onFilterByChange={setRecentPayoutSearchField}
            filterOptions={recentPayoutSearchOptions}
            placeholder={`Search by ${recentPayoutSearchField}`}
            className="w-full max-w-[620px]"
          />
        }
        className="payout-dashboard-table"
        columns={[
          "Reference ID",
          "UTR Number",
          "Beneficiary",
          "Purpose",
          "Amount",
          "Status",
          "Settlement Time",
          "Actions",
        ]}
        rows={recentPayoutTableRows}
        minWidth="min-w-[1080px]"
        emptyState={
          <PayoutEmptyState
            icon={<LuCheck />}
            title="No payouts yet"
            description="Completed payout activity will appear here once the first transfer is processed."
            action={
              canAccess("transfers.create") ? (
                <Link
                  to="/payouts/transfers/initiate"
                  className="flex h-10 items-center gap-2 rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
                >
                  <FiPlus /> Initiate Transfer
                </Link>
              ) : null
            }
          />
        }
        footer={
          <div className="flex min-h-[64px] w-full items-center justify-between gap-4 bg-[#F6F8FA] px-6 py-4 text-xs font-medium text-[#505E8E]">
            <span className="min-w-max">
              Showing {recentPayoutStart} to {recentPayoutEnd} of{" "}
              {filteredRecentPayouts.length} results
            </span>
            <div className="ml-auto flex min-w-max items-center gap-2">
              <button
                disabled={recentPayoutPage === 1}
                onClick={() =>
                  setRecentPayoutPage((current) => Math.max(1, current - 1))
                }
                className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaArrowLeft className="text-xs" />
              </button>
              {Array.from({ length: recentPayoutPageCount }, (_, index) => {
                const page = index + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setRecentPayoutPage(page)}
                    className={
                      "h-9 w-9 rounded-md " +
                      (recentPayoutPage === page
                        ? "bg-[#1B163B] text-white"
                        : "border border-[#E0E5F2] text-[#1B163B]")
                    }
                  >
                    {page}
                  </button>
                );
              })}
              <button
                disabled={recentPayoutPage === recentPayoutPageCount}
                onClick={() =>
                  setRecentPayoutPage((current) =>
                    Math.min(recentPayoutPageCount, current + 1),
                  )
                }
                className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaArrowRight className="text-xs" />
              </button>
            </div>
          </div>
        }
      />
      <StatementDetailsDrawer
        row={selectedStatement}
        onClose={() => setSelectedStatement(null)}
      />
    </div>
  );
}

function PayoutAccountsPage() {
  const { canAccess } = usePayoutAccess();
  const accountResource = usePayoutResource(
    [] as PayoutAccount[],
    (signal) =>
      payoutApi.accounts(signal).then((rows) =>
        rows.map((row) => accountFromApiRecord(row, emptyAccountFallback)),
      ),
  );
  const accounts = accountResource.data;

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/payouts/dashboard"
            className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-[#6687FF]"
          >
            <FaArrowLeft className="text-xs" />
            Back to Dashboard
          </Link>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Payout Accounts
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            View all school payout accounts used to make beneficiary payments.
          </p>
        </div>
        {canAccess("accounts.create") && (
          <Link
            to="/payouts/accounts/new"
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
            className="mt-8 flex h-10 items-center gap-3 rounded-[6px] bg-edviron_black px-5 text-sm font-semibold text-white shadow-sm hover:bg-edviron_black/90"
          >
            <FiPlus className="text-lg" />
            New Account
          </Link>
        )}
      </div>

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {accounts.map((account) => (
          <PayoutAccountCard key={account.id} account={account} />
        ))}
      </div>
      {!accounts.length && (
        <div className="rounded-[8px] border border-[#E0E5F2] bg-white new-shadow">
          <PayoutEmptyState
            icon={<LuLandmark />}
            title="No payout accounts found"
            description="Create a school payout account to start beneficiary payments, settlements and account-level access assignment."
            action={
              canAccess("accounts.create") ? (
                <Link
                  to="/payouts/accounts/new"
                  className="flex h-10 items-center gap-2 rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
                >
                  <FiPlus /> New Account
                </Link>
              ) : null
            }
          />
        </div>
      )}
    </div>
  );
}

function ProtectedPayoutRoute({
  permission,
  children,
}: {
  permission: PayoutPermission;
  children: React.ReactElement;
}) {
  const { canAccess } = usePayoutAccess();
  if (!canAccess(permission)) {
    return <Navigate to="/payouts/dashboard" replace />;
  }
  return children;
}

function Payouts({ menu }: { menu: boolean }) {
  return (
    <div className="payout-module flex">
      <PayoutNotification />
      <PayoutModuleSidebar menu={menu} />
      <div
        className={
          "tab-content w-full py-4 transition-all duration-200 " +
          (menu ? "pl-64" : "pl-2")
        }
      >
        <Routes>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<PayoutDashboardHome />} />
          <Route path="accounts/new" element={<ProtectedPayoutRoute permission="accounts.create"><PayoutAddAccount /></ProtectedPayoutRoute>} />
          <Route path="accounts/:accountId" element={<ProtectedPayoutRoute permission="accounts.view"><PayoutAccountOverview /></ProtectedPayoutRoute>} />
          <Route path="accounts" element={<PayoutAccountsPage />} />
          <Route path="statements" element={<PayoutStatements />} />
          <Route path="transfers" element={<PayoutTransfers />} />
          <Route path="transfers/initiate" element={<ProtectedPayoutRoute permission="transfers.create"><PayoutInitiateTransfer /></ProtectedPayoutRoute>} />
          <Route path="transfers/initiate/review" element={<ProtectedPayoutRoute permission="transfers.create"><PayoutReviewTransfer /></ProtectedPayoutRoute>} />
          <Route path="transfers/initiate/verify" element={<ProtectedPayoutRoute permission="transfers.create"><PayoutVerifyTransfer /></ProtectedPayoutRoute>} />
          <Route path="transfers/initiate/confirm" element={<ProtectedPayoutRoute permission="transfers.create"><PayoutConfirmTransfer /></ProtectedPayoutRoute>} />
          <Route path="transfers/bulk-upload" element={<ProtectedPayoutRoute permission="transfers.create"><PayoutBulkUploadTransfer /></ProtectedPayoutRoute>} />
          <Route path="transfers/bulk-upload/review" element={<ProtectedPayoutRoute permission="transfers.create"><PayoutBulkUploadReview /></ProtectedPayoutRoute>} />
          <Route path="transfers/bulk-upload/verify" element={<ProtectedPayoutRoute permission="transfers.create"><PayoutBulkUploadVerify /></ProtectedPayoutRoute>} />
          <Route path="transfers/bulk-upload/confirm" element={<ProtectedPayoutRoute permission="transfers.create"><PayoutBulkUploadConfirm /></ProtectedPayoutRoute>} />
          <Route path="transfers/pending-approval/bulk-approve/verify" element={<ProtectedPayoutRoute permission="transfers.approve"><PayoutBulkApprovalVerify /></ProtectedPayoutRoute>} />
          <Route path="transfers/pending-approval/:action/verify" element={<ProtectedPayoutRoute permission="transfers.approve"><PayoutApprovalActionVerify /></ProtectedPayoutRoute>} />
          <Route path="transfers/pending-approval/:action/confirm" element={<ProtectedPayoutRoute permission="transfers.approve"><PayoutApprovalActionConfirm /></ProtectedPayoutRoute>} />
          <Route path="transfers/pending-approval" element={<PayoutPendingApproval />} />
          <Route path="pending-approval" element={<Navigate to="transfers/pending-approval" replace />} />
          <Route path="history" element={<Navigate to="transfers" replace />} />
          <Route path="bulk-upload" element={<Navigate to="transfers/bulk-upload" replace />} />
          <Route path="beneficiaries" element={<PayoutBeneficiaries />} />
          <Route path="beneficiaries/view/:beneficiaryId" element={<PayoutBeneficiaryDetails />} />
          <Route path="beneficiaries/edit/:beneficiaryId" element={<ProtectedPayoutRoute permission="beneficiaries.manage"><PayoutEditBeneficiary /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/edit/:beneficiaryId/review" element={<ProtectedPayoutRoute permission="beneficiaries.manage"><PayoutEditBeneficiaryReview /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/edit/:beneficiaryId/verify" element={<ProtectedPayoutRoute permission="beneficiaries.manage"><PayoutEditBeneficiaryVerify /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/edit/:beneficiaryId/confirm" element={<ProtectedPayoutRoute permission="beneficiaries.manage"><PayoutEditBeneficiaryConfirm /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/delete/:beneficiaryId/verify" element={<ProtectedPayoutRoute permission="beneficiaries.delete"><PayoutDeleteBeneficiaryVerify /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/delete/:beneficiaryId/confirm" element={<ProtectedPayoutRoute permission="beneficiaries.delete"><PayoutDeleteBeneficiaryConfirm /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/add" element={<ProtectedPayoutRoute permission="beneficiaries.manage"><PayoutAddBeneficiary /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/add/review" element={<ProtectedPayoutRoute permission="beneficiaries.manage"><PayoutAddBeneficiaryReview /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/add/verify" element={<ProtectedPayoutRoute permission="beneficiaries.manage"><PayoutAddBeneficiaryVerify /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/add/confirm" element={<ProtectedPayoutRoute permission="beneficiaries.manage"><PayoutAddBeneficiaryConfirm /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/bulk-upload" element={<ProtectedPayoutRoute permission="beneficiaries.manage"><PayoutBeneficiaryBulkUpload /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/bulk-upload/review" element={<ProtectedPayoutRoute permission="beneficiaries.manage"><PayoutBeneficiaryBulkUploadReview /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/bulk-upload/verify" element={<ProtectedPayoutRoute permission="beneficiaries.manage"><PayoutBeneficiaryBulkUploadVerify /></ProtectedPayoutRoute>} />
          <Route path="beneficiaries/bulk-upload/confirm" element={<ProtectedPayoutRoute permission="beneficiaries.manage"><PayoutBeneficiaryBulkUploadConfirm /></ProtectedPayoutRoute>} />
          <Route path="users" element={<ProtectedPayoutRoute permission="users.view"><PayoutUsers /></ProtectedPayoutRoute>} />
          <Route path="users/add" element={<ProtectedPayoutRoute permission="users.manage"><PayoutAddUser /></ProtectedPayoutRoute>} />
          <Route path="users/view/:userId" element={<ProtectedPayoutRoute permission="users.view"><PayoutUserDetails /></ProtectedPayoutRoute>} />
          <Route path="users/:action/:userId/verify" element={<ProtectedPayoutRoute permission="users.manage"><PayoutUserActionVerify /></ProtectedPayoutRoute>} />
          <Route path="users/edit/:userId" element={<ProtectedPayoutRoute permission="users.manage"><PayoutEditUser /></ProtectedPayoutRoute>} />
          <Route path="users/delete/:userId" element={<ProtectedPayoutRoute permission="users.manage"><PayoutDeleteUserConfirm /></ProtectedPayoutRoute>} />
          <Route path="users/delete/:userId/complete" element={<ProtectedPayoutRoute permission="users.manage"><PayoutDeleteUserComplete /></ProtectedPayoutRoute>} />
          <Route path="users/change-password/:userId" element={<ProtectedPayoutRoute permission="users.manage"><PayoutChangeUserPassword /></ProtectedPayoutRoute>} />
          <Route path="roles" element={<ProtectedPayoutRoute permission="roles.view"><PayoutRoles /></ProtectedPayoutRoute>} />
          <Route path="roles/:roleSlug" element={<ProtectedPayoutRoute permission="roles.view"><PayoutRoleDetails /></ProtectedPayoutRoute>} />
          <Route path="permissions" element={<ProtectedPayoutRoute permission="permissions.view"><PayoutPermissions /></ProtectedPayoutRoute>} />
          <Route path="approval-rules/*" element={<ProtectedPayoutRoute permission="approvalRules.manage"><PayoutApprovalRules /></ProtectedPayoutRoute>} />
          <Route path="reports" element={<PayoutReports />} />
          <Route path="reports/insights" element={<PayoutInsights />} />
          <Route path="settings" element={<ProtectedPayoutRoute permission="settings.manage"><PayoutSettings /></ProtectedPayoutRoute>} />
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export default Payouts;
